import { useEffect } from 'react';
import { BUTTON_THEMES, Form, InputField, LoaderComponent } from 'ht-styleguide';
import { useForm, SubmitHandler } from 'react-hook-form';

/* Store */
import { useHomeHubStoreUserFlowAttributes } from '@scenes/HomeHub/store/client/homehub.store';

/* Queries */
import useUserMutationLogin from '@store/queries/user/useUserMutation.login';
import useUserMutationResetPassword from '@store/queries/user/useUserMutation.resetPassword';

/* Selectors/ Hooks */
import { useUserIdSelector } from '@store/queries/user/useUser.selector';

/* Components */
import UserFlowHeader from '@scenes/HomeHub/pages/User/components/Header';
import ButtonWrapper from '@scenes/HomeHub/pages/User/components/ButtonWrapper';
import { GridLayoutForm } from '@scenes/HomeHub/pages/User/components/GridLayouts';
import UserFlowHeaderIcon from '@scenes/HomeHub/pages/User/components/ImageIcon';
import Typography from '@scenes/HomeHub/pages/User/components/Typography';
import PlainButton from '@components/UI/PlainButton';
import Either from '@components/Either';
import Maybe from '@components/Maybe';

/* Hooks */
import useRouteController from '@scenes/HomeHub/hooks/hook.routeController';

/* Integrations */
import { TRACKING_TYPES } from '@integrations/segment/homehub';

/* Constants / Utils */
import { YupSchemaResolver } from '@constants/validations';
import { setErrorsHookFormErrors } from '@utils/errors';
import { TUserPageProps, UserScreenTypes } from '@scenes/HomeHub/pages/User/index';

import { TLoginForm } from 'types/form.types';

import styles from './user.module.scss';

const UserPasswordStep = ({ userSegmentTracking }: TUserPageProps) => {
  /* Store */
  const { userEmail, firstName } = useHomeHubStoreUserFlowAttributes();

  /* Queries & Mutations */
  const { data, mutate: mutateLogin, isPending } = useUserMutationLogin();
  const { mutate: resetPassword } = useUserMutationResetPassword();

  /* Hooks */
  const { surveyRoute, nextUserRoute } = useRouteController();
  const userID = useUserIdSelector();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isDirty },
  } = useForm<TLoginForm>({ ...YupSchemaResolver(['password']) });

  /* Methods */
  const handleResetPassword = () =>
    resetPassword(userEmail, {
      onSuccess: () => nextUserRoute(UserScreenTypes.emailPassword),
    });

  const loginFormAttributes: SubmitHandler<TLoginForm> = ({ password }) => {
    mutateLogin(
      { email: userEmail, password },
      {
        onSuccess: (d): any => {
          if (!d.error) {
            surveyRoute();
            userSegmentTracking(TRACKING_TYPES.finishedSurvey);
          }
        },
      },
    );

    return false;
  };

  /* Resaturate Errors */
  useEffect(() => {
    if (data?.error) {
      setErrorsHookFormErrors<TLoginForm>({ setError, mutationData: data, field: 'password' });
    }
  }, [data?.error, data, setError]);

  const firstNameDisplay = firstName ? `, ${firstName}` : '';

  return (
    <GridLayoutForm>
      <UserFlowHeaderIcon width="104" height="104" imageName="illus-lock" />
      <Maybe condition={firstNameDisplay}>
        <Typography>Welcome Back{firstNameDisplay}!</Typography>
      </Maybe>
      <Form.Row classes="marginAuto width100">
        <Form.Column>
          <UserFlowHeader classes="paddingTop-small1" headerText="To continue, please enter your password." />
          <Typography className="p1 n700 paddingTop-tiny1">This prevents others from accessing any information you share with us.</Typography>
        </Form.Column>
      </Form.Row>
      <Form.Row classes="marginAuto width100">
        <Form.Column sm={4} md={6} mdOffset={1} lg={4} lgOffset={4}>
          <Either condition={isPending || userID}>
            <div className="paddingTop-small">
              <LoaderComponent />
            </div>

            <div className={styles.actionContainer}>
              <InputField placeholder="Enter your password" type="password" label="" error={errors.password?.message} {...register('password')} />
              <ButtonWrapper
                className="paddingTop-small1 paddingBottom-tiny1"
                disabled={!isDirty}
                onClick={handleSubmit(loginFormAttributes)}
                theme={BUTTON_THEMES.PRIMARY}
              >
                Continue the Survey
              </ButtonWrapper>
              <div className="paddingTop-small1 text-align-center">
                <PlainButton onClickHandler={handleResetPassword}>Forgot your password?</PlainButton>
              </div>
            </div>
          </Either>
        </Form.Column>
      </Form.Row>
    </GridLayoutForm>
  );
};

export default UserPasswordStep;
